/**
 * About card aligned with @backstage/plugin-catalog EntityAboutCard, but:
 * - Metadata **Edit** renders only when catalog-edit-check **edit** allows (not merely in DOM hidden).
 * - **View Source** in the icon row omits the SCM link when view-source check denies (gated kinds).
 *
 * Injects derived `backstage.io/edit-url` when missing (GitLab templates), same as before.
 */

import { useCallback, useMemo, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import CachedIcon from '@material-ui/icons/Cached';
import EditIcon from '@material-ui/icons/Edit';
import DocsIcon from '@material-ui/icons/Description';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import {
  Link,
  AppIcon,
  HeaderIconLinkRow,
} from '@backstage/core-components';
import {
  useApi,
  alertApiRef,
  errorApiRef,
  useRouteRef,
  useApp,
} from '@backstage/core-plugin-api';
import { scmIntegrationsApiRef, ScmIntegrationIcon } from '@backstage/integration-react';
import {
  ANNOTATION_EDIT_URL,
  ANNOTATION_LOCATION,
  stringifyEntityRef,
  DEFAULT_NAMESPACE,
  parseEntityRef,
} from '@backstage/catalog-model';
import type { CompoundEntityRef, Entity } from '@backstage/catalog-model';
import {
  EntityProvider,
  useEntity,
  catalogApiRef,
  getEntitySourceLocation,
} from '@backstage/plugin-catalog-react';
import { useEntityPermission } from '@backstage/plugin-catalog-react/alpha';
import { catalogEntityRefreshPermission } from '@backstage/plugin-catalog-common/alpha';
import { buildTechDocsURL } from '@backstage/plugin-techdocs-react';
import { taskCreatePermission } from '@backstage/plugin-scaffolder-common/alpha';
import { usePermission } from '@backstage/plugin-permission-react';
import {
  AboutContent,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { resolveCatalogWebEditUrl } from './resolveCatalogWebEditUrl';
import { useCatalogEntityPagePermissions } from './catalogEntityPagePermissionsContext';

const SOURCE_TEMPLATE_ANNOTATION = 'backstage.io/source-template';
/** Same keys as @backstage/plugin-techdocs-common (avoid extra app dependency). */
const TECHDOCS_ANNOTATION = 'backstage.io/techdocs-ref';
const TECHDOCS_EXTERNAL_ANNOTATION = 'backstage.io/techdocs-entity';

const useStyles = makeStyles({
  gridItemCard: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100% - 10px)',
    marginBottom: '10px',
  },
  fullHeightCard: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  gridItemCardContent: { flex: 1 },
  fullHeightCardContent: { flex: 1 },
});

function useSourceTemplateCompoundEntityRef(
  entity: Entity,
): CompoundEntityRef | undefined {
  const catalogApi = useApi(catalogApiRef);
  const [sourceTemplateRef, setSourceTemplateRef] = useState<
    CompoundEntityRef | undefined
  >(undefined);

  useEffect(() => {
    let cancelled = false;
    const refCandidate = entity.metadata.annotations?.[SOURCE_TEMPLATE_ANNOTATION];
    if (!refCandidate) {
      setSourceTemplateRef(undefined);
      return () => {};
    }
    (async () => {
      try {
        const template = await catalogApi.getEntityByRef(refCandidate);
        const compoundRefCandidate = parseEntityRef(refCandidate);
        if (!cancelled) {
          setSourceTemplateRef(template !== undefined ? compoundRefCandidate : undefined);
        }
      } catch {
        if (!cancelled) setSourceTemplateRef(undefined);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [catalogApi, entity]);

  return sourceTemplateRef;
}

function GatedAboutCardSubheader(): JSX.Element {
  const { entity } = useEntity();
  const { gatesViewSource, viewSourceAllowed, onEntityPage } =
    useCatalogEntityPagePermissions();
  const scmIntegrationsApi = useApi(scmIntegrationsApiRef);
  const viewTechDocLink = useRouteRef(catalogPlugin.externalRoutes.viewTechDoc);
  const templateRoute = useRouteRef(catalogPlugin.externalRoutes.createFromTemplate);
  const app = useApp();
  const { allowed: canCreateTemplateTask } = usePermission({
    permission: taskCreatePermission,
  });

  const entitySourceLocation = getEntitySourceLocation(
    entity,
    scmIntegrationsApi,
  );

  // Require a parsed catalog entity URL (same as metadata Edit); otherwise default
  // context has gatesViewSource=false and would incorrectly show the link.
  const showViewSource =
    onEntityPage && (!gatesViewSource || viewSourceAllowed === true);

  const catalogSourceIconLink = {
    label: 'View Source',
    disabled: !entitySourceLocation,
    icon: (
      <ScmIntegrationIcon type={entitySourceLocation?.integrationType} />
    ),
    href: entitySourceLocation?.locationTargetUrl,
  };

  const techdocsreaderIconLink = {
    label: 'View TechDocs',
    disabled:
      !(
        entity.metadata.annotations?.[TECHDOCS_ANNOTATION] ||
        entity.metadata.annotations?.[TECHDOCS_EXTERNAL_ANNOTATION]
      ) || !viewTechDocLink,
    icon: <DocsIcon />,
    href: buildTechDocsURL(entity, viewTechDocLink),
  };

  const Icon = app.getSystemIcon('scaffolder') ?? CreateComponentIcon;
  const scaffolderTemplateIconLink = {
    label: 'Launch Template',
    icon: <Icon />,
    disabled: !templateRoute || !canCreateTemplateTask,
    href:
      templateRoute &&
      templateRoute({
        templateName: entity.metadata.name,
        namespace: entity.metadata.namespace || DEFAULT_NAMESPACE,
      }),
  };

  const links = [];
  if (showViewSource) {
    links.push(catalogSourceIconLink);
  }
  links.push(techdocsreaderIconLink);
  if (entity.kind?.toLowerCase() === 'template') {
    links.push(scaffolderTemplateIconLink);
  }

  return <HeaderIconLinkRow links={links} />;
}

function GatedAboutCardInner(props: {
  variant?: 'gridItem' | 'fullHeight';
}): JSX.Element {
  const classes = useStyles();
  const variant = props.variant ?? 'gridItem';
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  const errorApi = useApi(errorApiRef);
  const templateRoute = useRouteRef(catalogPlugin.externalRoutes.createFromTemplate);
  const sourceTemplateRef = useSourceTemplateCompoundEntityRef(entity);
  const { allowed: canRefresh } = useEntityPermission(catalogEntityRefreshPermission);
  const { editAllowed, onEntityPage } = useCatalogEntityPagePermissions();

  const entityMetadataEditUrl =
    entity.metadata.annotations?.[ANNOTATION_EDIT_URL]?.trim() || undefined;
  const entityLocation = entity.metadata.annotations?.[ANNOTATION_LOCATION];
  const allowRefresh =
    entityLocation?.startsWith('url:') || entityLocation?.startsWith('file:');

  const showMetadataEdit =
    onEntityPage &&
    editAllowed === true &&
    Boolean(entityMetadataEditUrl);

  const refreshEntity = useCallback(async () => {
    try {
      await catalogApi.refreshEntity(stringifyEntityRef(entity));
      alertApi.post({
        message: 'Refresh scheduled',
        severity: 'info',
        display: 'transient',
      });
    } catch (e: unknown) {
      const err = e instanceof Error ? e : new Error(String(e));
      errorApi.post({
        name: err.name,
        message: err.message,
        stack: err.stack,
      });
    }
  }, [catalogApi, entity, alertApi, errorApi]);

  let cardClass = '';
  if (variant === 'gridItem') cardClass = classes.gridItemCard;
  else if (variant === 'fullHeight') cardClass = classes.fullHeightCard;

  let cardContentClass = '';
  if (variant === 'gridItem') cardContentClass = classes.gridItemCardContent;
  else if (variant === 'fullHeight') cardContentClass = classes.fullHeightCardContent;

  return (
    <Card className={cardClass}>
      <CardHeader
        title="About"
        action={
          <>
            {allowRefresh && canRefresh && (
              <IconButton
                aria-label="Refresh"
                title="Schedule entity refresh"
                onClick={refreshEntity}
              >
                <CachedIcon />
              </IconButton>
            )}
            {showMetadataEdit && (
              <IconButton
                component={Link}
                aria-label="Edit"
                title="Edit Metadata"
                to={entityMetadataEditUrl!}
              >
                <EditIcon />
              </IconButton>
            )}
            {sourceTemplateRef && templateRoute && (
              <IconButton
                component={Link}
                title="Create something similar"
                to={templateRoute({
                  namespace: sourceTemplateRef.namespace,
                  templateName: sourceTemplateRef.name,
                })}
              >
                <AppIcon id="scaffolder" />
              </IconButton>
            )}
          </>
        }
        subheader={<GatedAboutCardSubheader />}
      />
      <Divider />
      <CardContent className={cardContentClass}>
        <AboutContent entity={entity} />
      </CardContent>
    </Card>
  );
}

export function EntityAboutCardWithEditGate(props: {
  variant?: 'gridItem' | 'fullHeight';
}): JSX.Element {
  const { entity } = useEntity();
  const variant = props.variant ?? 'gridItem';

  const augmentedEntity = useMemo((): Entity => {
    const existing = entity.metadata?.annotations?.[ANNOTATION_EDIT_URL]?.trim();
    if (existing) return entity;
    const derived = resolveCatalogWebEditUrl(entity);
    if (!derived) return entity;
    return {
      ...entity,
      metadata: {
        ...entity.metadata,
        annotations: {
          ...entity.metadata.annotations,
          [ANNOTATION_EDIT_URL]: derived,
        },
      },
    };
  }, [entity]);

  if (augmentedEntity === entity) {
    return <GatedAboutCardInner variant={variant} />;
  }

  return (
    <EntityProvider entity={augmentedEntity}>
      <GatedAboutCardInner variant={variant} />
    </EntityProvider>
  );
}
